import React, { useState } from 'react';
import Button from './Button.jsx';
import api from '../services/api';
import { BEE_SECTOR } from './CompanyInfoFields.jsx';

export const ENTITY_SIZE = ['Generic', 'QSE'];

const CreateFinancialYearForm = ({ companyId, companies = [], onCreated }) => {
  const [formData, setFormData] = useState({
    company_id: companyId || '',
    start_date: '',
    end_date: '',
    label: '',
    entity_size: '',
    bee_sector: '',
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [endDateTouched, setEndDateTouched] = useState(false);

  const calculateEndDate = (startDate) => {
    const start = new Date(`${startDate}T00:00:00`);
    if (Number.isNaN(start.getTime())) return '';
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + 1);
    end.setDate(end.getDate() - 1);
    return end.toISOString().slice(0, 10);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'end_date') {
      setEndDateTouched(true);
    }

    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'start_date' && !endDateTouched) {
        next.end_date = calculateEndDate(value);
      }
      return next;
    });
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = {};
    if (!companyId && !formData.company_id) newErrors.company_id = 'Company is required.';
    if (!formData.start_date) newErrors.start_date = 'Start date is required.';
    if (!formData.end_date) newErrors.end_date = 'End date is required.';
    if (!formData.entity_size) newErrors.entity_size = 'Entity size is required.';
    if (!formData.bee_sector) newErrors.bee_sector = 'BEE sector is required.';
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }

    setIsLoading(true);
    try {
      const res = await api.post('/core/financial-years/create/', {
        ...formData,
        company_id: companyId || formData.company_id,
      });
      setFormData({
        company_id: companyId || '',
        start_date: '',
        end_date: '',
        label: '',
        entity_size: '',
        bee_sector: '',
      });
      setEndDateTouched(false);
      onCreated(res.data);
    } catch (error) {
      const data = error.response?.data;
      if (data && typeof data === 'object') {
        setErrors({ ...data, general: data.error || data.erorr || undefined });
      } else {
        setErrors({ general: 'Failed to create financial year. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {errors.general && <div className="error-banner">{errors.general}</div>}

      {!companyId && (
        <div className="add-user-field">
          <label className="input-label">Company</label>
          <select
            name="company_id"
            value={formData.company_id}
            onChange={handleChange}
            className="ci-select"
          >
            <option value="">— Select a company —</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {errors.company_id && <span className="input-error">{errors.company_id}</span>}
        </div>
      )}

      <div className="add-user-grid">
        <div className="add-user-field">
          <label className="input-label">Start Date</label>
          <input
            type="date"
            name="start_date"
            value={formData.start_date}
            onChange={handleChange}
            className="ci-select"
          />
          {errors.start_date && <span className="input-error">{errors.start_date}</span>}
        </div>
        <div className="add-user-field">
          <label className="input-label">End Date</label>
          <input
            type="date"
            name="end_date"
            value={formData.end_date}
            onChange={handleChange}
            className="ci-select"
          />
          {errors.end_date && <span className="input-error">{errors.end_date}</span>}
        </div>
      </div>

      <div className="add-user-field">
        <label className="input-label">
          Label <span className="form-hint">(optional — defaults to FY year)</span>
        </label>
        <input
          type="text"
          name="label"
          value={formData.label}
          onChange={handleChange}
          placeholder="e.g. FY2025"
          className="ci-select"
        />
      </div>

      <div className="add-user-field">
        <label className="input-label">Entity Size</label>
        <select
          name="entity_size"
          value={formData.entity_size}
          onChange={handleChange}
          className="ci-select"
        >
          <option value="">— Select Entity Size —</option>
          {ENTITY_SIZE.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {errors.entity_size && <span className="input-error">{errors.entity_size}</span>}
      </div>

      <div className="add-user-field">
        <label className="input-label">BEE Sector Code</label>
        <select
          name="bee_sector"
          value={formData.bee_sector}
          onChange={handleChange}
          className="ci-select"
        >
          <option value="">— Select BEE Sector —</option>
          {BEE_SECTOR.map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        {errors.bee_sector && <span className="input-error">{errors.bee_sector}</span>}
      </div>

      <Button type="submit" fullWidth disabled={isLoading}>
        {isLoading ? 'Creating...' : 'Create Financial Year'}
      </Button>
    </form>
  );
};

export default CreateFinancialYearForm;
